/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import { useEffect, useState } from "react";
import { Card } from "@material-ui/core";
import { v4 as uuid } from "uuid";
import {
	ContentLayout,
	LeftPanelLayout,
	PanelContainer,
	ProgressOverlay,
	RightPanelLayout,
	ShowSnackbar,
} from "../../../components/Common";
import { SUPPORTED_LANGUAGES, TERMINOLOGY_FILE_NAME } from "../../../utility/constants";
import {
	deleteTerminologyFileAsync,
	getTerminologyFileDataAsync,
	updateTerminologyFileAsync,
} from "../services/TranslateService";
import { LangTermList } from "../components/TranslationTerminology/LangTermsList";
import { LangTermDetails } from "../components/TranslationTerminology/LangTermDetails";
import { v2 } from "@prftesp/cle-logger";
import { useCallback } from "react";

const sanitizeIds = (data) =>
	data.map((row) =>
		row.map((term) => {
			const newTerm = { ...term };
			delete newTerm.id;
			return newTerm;
		}),
	);

const saveTermTranslations = async (sourceLangCode, file) => {
	try {
		let response;
		if (file) {
			response = await updateTerminologyFileAsync({
				data: file,
				sourceLangCode,
			});
		} else {
			response = await deleteTerminologyFileAsync();
		}
		return { success: true, data: response };
	} catch (error) {
		v2.warn("Failed to update terminology. Attempting to retry", {
			additionalData: { language: sourceLangCode, file },
		});
		return { success: false, error: error?.response?.data?.error };
	}
};

const getTerminologyFileName = (languageCode) => `${TERMINOLOGY_FILE_NAME}${languageCode}`;

// Start - Backward compatibility - remove once migration completed.
const checkOtherLangTerminologiesFiles = async () => {
	try {
		for (let idx = 1; idx < SUPPORTED_LANGUAGES.length; idx++) {
			const language = SUPPORTED_LANGUAGES[idx];
			let result = await getTerminologyFileDataAsync(getTerminologyFileName(language.languageCode));
			if (result.data) {
				return result;
			}
		}
		return {};
	} catch (error) {
		return {};
	}
};
// End - Backward compatibility.

const TranslationTerminologyPage = () => {
	const [supportedLanguages] = useState(SUPPORTED_LANGUAGES);
	const [sourceLanguage, setSourceLanguage] = useState({ ...SUPPORTED_LANGUAGES[0] }); // English as default language.
	const [translationsData, setTranslationsData] = useState([]);

	const [formEdited, setFormEdited] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [message, setMessage] = useState({ message: "", severity: "" });
	const [activeTerm, setActiveTerm] = useState({ isNew: false, term: undefined });

	const handleSnackBarClose = () => {
		setMessage((cur) => ({ ...cur, message: "" }));
	};

	const handleCancelChanges = () => {
		setFormEdited(false);
		setActiveTerm({ isNew: false, term: undefined });
	};

	const fetchLangTranslations = useCallback(async () => {
		try {
			setIsLoading(true);
			let result = await getTerminologyFileDataAsync(getTerminologyFileName(SUPPORTED_LANGUAGES[0].languageCode)); // English
			if (!result.data) {
				result = checkOtherLangTerminologiesFiles();
			}
			const { sourceLangCode, data } = result;
			const sourceLang = supportedLanguages.find((lang) => lang.languageCode === sourceLangCode);

			if (sourceLang && data) {
				const modifiedData = data.map((row) => row.map((term) => ({ ...term, id: uuid() })));
				setSourceLanguage(sourceLang); // helps to load terms on left panel.
				setTranslationsData(modifiedData);
				setFormEdited(false);
			}
		} catch (error) {
			if (error?.response?.data?.result?.error !== '"ResourceNotFoundException"') {
				setMessage({
					message: "Failed to process terminology file data.",
					severity: "error",
				});
			}
		} finally {
			setIsLoading(false);
		}
	}, [supportedLanguages]);

	useEffect(() => {
		fetchLangTranslations();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getSourceTermId = (row) => row.find((term) => term.languageCode === sourceLanguage?.languageCode)?.id;

	const handleSaveClick = async () => {
		setIsLoading(true);
		let translationsArr = translationsData;
		if (activeTerm.isNew) {
			// New Term translations
			translationsArr = [...translationsData, activeTerm.term];
		} else {
			// Updating existing term translations.
			const activeTermSourceId = getSourceTermId(activeTerm.term);
			translationsArr = translationsData.map((row) =>
				getSourceTermId(row) === activeTermSourceId ? activeTerm.term : row,
			);
		}
		const saveResponse = await saveTermTranslations(sourceLanguage?.languageCode, sanitizeIds(translationsArr));
		if (saveResponse.success) {
			setTranslationsData(translationsArr);
			setMessage({
				message: `Successfully ${activeTerm?.isNew ? "created" : "saved"} term translations.`,
				severity: "success",
			});
			setActiveTerm((c) => ({ isNew: false, term: c.term }));
			setFormEdited(false);
		} else {
			setMessage({
				message: saveResponse.error || `Failed to ${activeTerm?.isNew ? "create" : "save"} term translations.`,
				severity: "error",
			});
		}
		setIsLoading(false);
	};

	const handleRemoveClick = async () => {
		setIsLoading(true);
		let removeResponse = {};
		const activeTermSourceId = getSourceTermId(activeTerm.term);
		const translationsArr = translationsData.filter((row) => getSourceTermId(row) !== activeTermSourceId);
		if (translationsArr.length > 0) {
			removeResponse = await saveTermTranslations(sourceLanguage?.languageCode, sanitizeIds(translationsArr));
		} else {
			removeResponse = await deleteTerminologyFileAsync();
		}
		if (removeResponse.success) {
			setTranslationsData(translationsArr);
			setActiveTerm({ isNew: false, term: undefined });
			setMessage({
				message: "Successfully removed term translations.",
				severity: "success",
			});
			setFormEdited(false);
		} else {
			setMessage({
				message: removeResponse.error || "Failed to remove term translations.",
				severity: "error",
			});
		}
		setIsLoading(false);
	};

	const addLangTerm = () => {
		const newTerm = [
			{
				languageCode: sourceLanguage.languageCode,
				value: "",
				id: uuid(),
			},
			{
				languageCode: "",
				value: "",
				id: uuid(),
			},
		];
		setActiveTerm({ isNew: true, term: newTerm });
		setFormEdited(true);
	};

	const updateActiveTerm = (term) => {
		setFormEdited(true);
		setActiveTerm((c) => ({ ...c, term }));
	};

	return (
		<ContentLayout>
			<ShowSnackbar severity={message.severity} message={message.message} onClose={handleSnackBarClose} />
			{isLoading && <ProgressOverlay />}
			<PanelContainer>
				<LeftPanelLayout>
					<Card style={{ minHeight: 600, height: "100%", padding: "10px 5px" }}>
						<LangTermList
							sourceLanguage={sourceLanguage}
							translationsData={translationsData}
							supportedLanguages={supportedLanguages}
							setFormEdited={setFormEdited}
							languagesLookup
							addTermHandler={addLangTerm}
							selectTermHandler={setActiveTerm}
							activeTerm={activeTerm.term}
							formEdited={formEdited}
						/>
					</Card>
				</LeftPanelLayout>
				<RightPanelLayout>
					<Card>
						<LangTermDetails
							activeTerm={activeTerm.term}
							isNewTerm={activeTerm.isNew}
							sourceLanguage={sourceLanguage}
							translationsData={translationsData}
							setFormEdited={setFormEdited}
							onCancelChanges={handleCancelChanges}
							supportedLanguages={supportedLanguages}
							formEdited={formEdited}
							setIsLoading={setIsLoading}
							updateActiveTerm={updateActiveTerm}
							onSaveTranslations={handleSaveClick}
							onRemoveTranslations={handleRemoveClick}
						/>
					</Card>
				</RightPanelLayout>
			</PanelContainer>
		</ContentLayout>
	);
};

export default TranslationTerminologyPage;
